import React, { useState, useEffect } from 'react';
import Navbar from '../components/navbar';
import Footer from '../components/footer';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import zipCodeAreas from '../components/zipcode';
import Login from './checkout/login';
import BillingDetails from './checkout/billingaddress';
import Payment from './checkout/payment';

const CheckoutPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [billingAddress, setBillingAddress] = useState({
    firstName: '',
    lastName: '',
    country: 'United States',
    streetHouseApartment: '',
    apartment: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: ''
  });
  const [shippingMethod, setShippingMethod] = useState('Flat rate');
  const [paymentMethod, setPaymentMethod] = useState('Credit Card (Helcim)');
  const [showCardInput, setShowCardInput] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deliveryTime, setDeliveryTime] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [zipCodeError, setZipCodeError] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [deliveryTimeOptions, setDeliveryTimeOptions] = useState([
    '8am - 10am',
    '12pm - 2pm',
    '4pm - 6pm',
  ]);
  const [timeStatus, setTimeStatus] = useState({});
  const [isProcessingPayPal, setIsProcessingPayPal] = useState(false); // Add this state

  const calculateSubtotal = () => {
    let hasLargeBox = false;
    let hasSmallBox = false;
    let regularItemsTotal = 0;

    cartItems.forEach(item => {
      const orderType = item.orderType?.toLowerCase() || '';
      if (orderType.includes('large box')) {
        hasLargeBox = true;
      } else if (orderType.includes('small box')) {
        hasSmallBox = true;
      } else {
        const price = item.discountedPrice && orderType.includes('discount')
          ? parseFloat(item.discountedPrice.replace('$', ''))
          : parseFloat(item.price.replace('$', ''));
        regularItemsTotal += price * item.quantity;
      }
    });

    let total = regularItemsTotal;
    if (hasLargeBox) total += 99;
    if (hasSmallBox) total += 59;

    return total;
  };

  const cartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
  const subtotal = calculateSubtotal();
  const shippingCost = shippingMethod === 'Standard delivery' ? 3.5 : 9;
  const total = subtotal + shippingCost;

  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'zipCode') {
      if (value && !zipCodeAreas[value]) {
        setZipCodeError('We only deliver to Houston for now!');
      } else {
        setZipCodeError('');
      }
    }
    setBillingAddress({ ...billingAddress, [name]: value });
  };

  const handleShippingChange = (e) => {
    setShippingMethod(e.target.value);
    setDeliveryTime(null);
  };

  const toggleCardInput = () => {
    setShowCardInput(!showCardInput);
  };

  useEffect(() => {
    if (isSubmitting || isProcessingPayPal) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [isSubmitting, isProcessingPayPal]);

  const isFormValid = () => {
    const { firstName, lastName, streetHouseApartment, city, state, zipCode, phone, email } = billingAddress;
    const isValid = (
      firstName.trim() !== '' &&
      lastName.trim() !== '' &&
      streetHouseApartment.trim() !== '' &&
      city.trim() !== '' &&
      state.trim() !== '' &&
      zipCode.trim() !== '' &&
      !zipCodeError &&
      phone.trim() !== '' &&
      email.trim() !== '' &&
      (paymentMethod !== 'Direct bank transfer' || showCardInput) &&
      (shippingMethod === 'Standard delivery' ? deliveryTime !== null : true)
    );
    return isValid;
  };

  const handleOrderSubmit = async (paymentData) => {
    if (!isFormValid()) {
      toast.error('Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);

    try {
      const orderData = {
        billingAddress,
        shippingMethod,
        paymentMethod,
        total,
        deliveryTime: shippingMethod === 'Standard delivery' ? deliveryTime : 'Express Delivery',
        cartItems: cartItems.map(item => ({
          ...item,
          orderType: item.orderType || 'General'
        })),
        subtotal,
        shippingCost,
        orderType: cartItems.length > 0 ? cartItems[0].orderType || 'General' : 'General',
        paidsuccess: paymentMethod === 'Credit Card (Helcim)' ? JSON.stringify(paymentData) : 'Direct bank transfer - pending'
      };

      const token = localStorage.getItem('AficFreshtoken');

      const response = await axios.post(
        "https://afric-fresh-backend.onrender.com/order/submit-order",
        orderData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.status === 201) {
        const orderNumber = response.data.orderNumber;
        localStorage.removeItem('cartItems');
        toast.success('Order submitted successfully!');
        setIsSubmitting(false);
        setIsProcessingPayPal(false);
        navigate(`/OrderReceived/${orderNumber}`);
      }
    } catch (error) {
      console.error('Error submitting order:', error.response?.data || error.message);
      setIsSubmitting(false);
      setIsProcessingPayPal(false);
      toast.error('Failed to submit order. Please try again.');
    }
  };

  useEffect(() => {
    const currentHoustonTime = new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' });
    const currentHour = new Date(currentHoustonTime).getHours();
    const updatedStatus = {};
    deliveryTimeOptions.forEach((slot) => {
      const [start, end] = slot
        .replace('am', '')
        .replace('pm', '')
        .split(' - ')
        .map((time) => parseInt(time, 10) + (slot.includes('pm') && parseInt(time, 10) < 12 ? 12 : 0));
      updatedStatus[slot] = currentHour >= end ? 'Time not available' : '';
    });
    setTimeStatus(updatedStatus);
  }, [deliveryTimeOptions]);

  const updateCartCount = () => {
    const storedCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
    setCartCount(storedCartItems.reduce((acc, item) => acc + item.quantity, 0));
  };

  useEffect(() => {
    updateCartCount();
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    const token = localStorage.getItem('AficFreshtoken');
    const userDetails = localStorage.getItem('AficFreshuserDetails');
    if (token && userDetails) {
      setIsAuthenticated(true);
      const userData = JSON.parse(userDetails);
      setBillingAddress((prev) => ({
        ...prev,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        phone: userData.phoneNumber || '',
        streetHouseApartment: userData.address?.streetHouseApartment || '',
        city: userData.address?.city || '',
        state: userData.address?.state || '',
        zipCode: userData.address?.zipCode || '',
      }));
    }
  };

  if (!isAuthenticated) {
    return (
      <>
        <Navbar cartCount={cartCount} updateCartCount={updateCartCount} />
        <Login setIsAuthenticated={setIsAuthenticated} setBillingAddress={setBillingAddress} />
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar cartCount={cartCount} updateCartCount={updateCartCount} />
      <div className="container mx-auto p-6 lg:mt-[70px] mt-[130px] relative">
        {/* Full-page overlay for PayPal processing */}
        {isProcessingPayPal && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center z-50">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white mb-4"></div>
            <div className="text-white text-xl">Processing PayPal payment... Please wait.</div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[350px] max-sm:gap-10">
          <BillingDetails 
            billingAddress={billingAddress} 
            handleInputChange={handleInputChange} 
            zipCodeError={zipCodeError} 
          />
          <Payment 
            subtotal={subtotal}
            shippingMethod={shippingMethod}
            handleShippingChange={handleShippingChange}
            deliveryTime={deliveryTime}
            setDeliveryTime={setDeliveryTime}
            deliveryTimeOptions={deliveryTimeOptions}
            timeStatus={timeStatus}
            total={total}
            handleOrderSubmit={handleOrderSubmit}
            isFormValid={isFormValid}
            isSubmitting={isSubmitting}
            cartItems={cartItems}
            setIsProcessingPayPal={setIsProcessingPayPal}
          />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CheckoutPage;